import { useEffect, useState } from 'preact/hooks'
import type { BootResult } from '~/storage/boot'
import { countAllStores } from '~/storage/readAll'
import styles from './RecoveryPage.module.css'

interface RecoveryPageProps {
  result: Extract<BootResult, { status: 'recovery' | 'unavailable' }>
  onRetry: () => void
}

/**
 * Read-only recovery mode (D-028).
 *
 * Reached when a migration aborted, or when the stored data is newer than this
 * build. No write path is mounted here.
 *
 * Store contents are enumerated from `objectStoreNames` rather than from the
 * compiled schema, because after a failed upgrade the app code is newer than
 * the data it must read. Assuming the current schema would be exactly the way
 * to fail twice.
 */
export function RecoveryPage({ result, onRetry }: RecoveryPageProps) {
  const [counts, setCounts] = useState<Readonly<Record<string, number>> | undefined>(undefined)
  const db = result.status === 'recovery' ? result.db : undefined

  useEffect(() => {
    if (db === undefined) return
    let cancelled = false
    void countAllStores(db).then((value) => {
      if (!cancelled) setCounts(value)
    })
    return () => {
      cancelled = true
    }
  }, [db])

  const totalRecords =
    counts === undefined ? undefined : Object.values(counts).reduce((sum, n) => sum + n, 0)

  return (
    <main id="main-content" class={styles.screen} tabIndex={-1}>
      <h1 class={styles.title}>Project Sola could not start normally</h1>

      <p class={styles.lede}>{result.error.message}</p>

      {result.status === 'recovery' ? (
        <>
          <p>
            Your existing data was not changed. The update to the local database did not complete,
            so the previous version is still in place.
          </p>

          <dl class={styles.facts}>
            <div class={styles.fact}>
              <dt>Stored database version</dt>
              <dd>{result.storedVersion ?? 'unknown'}</dd>
            </div>
            <div class={styles.fact}>
              <dt>Version this build expects</dt>
              <dd>{result.expectedVersion}</dd>
            </div>
            {result.snapshot === undefined ? null : (
              <div class={styles.fact}>
                <dt>Recovery snapshot</dt>
                <dd>
                  Saved {new Date(result.snapshot.createdAt).toLocaleString()} (
                  {result.snapshot.sink})
                </dd>
              </div>
            )}
          </dl>

          <section aria-labelledby="records-heading">
            <h2 id="records-heading" class={styles.sectionTitle}>
              What is still stored
            </h2>
            {counts === undefined ? (
              <p>Reading…</p>
            ) : (
              <>
                <p>
                  {totalRecords} record{totalRecords === 1 ? '' : 's'} across{' '}
                  {Object.keys(counts).length} stores.
                </p>
                <ul class={styles.storeList}>
                  {Object.entries(counts)
                    .filter(([, count]) => count > 0)
                    .map(([name, count]) => (
                      <li key={name}>
                        <span class={styles.storeName}>{name}</span>
                        <span class={styles.storeCount}>{count}</span>
                      </li>
                    ))}
                </ul>
              </>
            )}
          </section>
        </>
      ) : null}

      <div class={styles.actions}>
        <button type="button" class={styles.primaryAction} onClick={onRetry}>
          Try again
        </button>
      </div>

      <p class={styles.note}>
        Saving a backup file from this screen is not available yet — it arrives with the backup and
        restore checkpoint. Until then, avoid clearing this browser's site data.
      </p>
    </main>
  )
}
